import { useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { ThinkingOrb, type OrbSize, type OrbState } from 'thinking-orbs'
import { cn } from '@/lib/utils'

/**
 * Our vocabulary → the library's nine states. Call sites use these words; the mapping lives
 * here only.
 */
export const ORB_STATES = {
  generating: 'weaving',
  testing: 'solving',
  walkthrough: 'working',
  running: 'working',
  searching: 'searching',
  evaluating: 'solving',
  thinking: 'solving',
  connecting: 'connecting',
  listening: 'listening',
} as const satisfies Record<string, OrbState>

export type OrbKind = keyof typeof ORB_STATES

/**
 * A design token's colour, read off an element that is INSIDE the token scope.
 *
 * The tokens live on `.v1`, not on the document root, so the only honest place to read one is
 * an element under it — this component's own wrapper. Re-read when the theme flips: the theme
 * is a class on <html>, and a token that differs between modes has to repaint the orb.
 */
function useTokenColor(ref: RefObject<HTMLElement | null>, name: string): string | null {
  const [value, setValue] = useState<string | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || typeof getComputedStyle !== 'function') return
    const read = () => {
      const next = getComputedStyle(el).getPropertyValue(name).trim()
      setValue((prev) => (prev === (next || null) ? prev : next || null))
    }
    read()
    if (typeof MutationObserver === 'undefined') return
    const observer = new MutationObserver(read)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'style'],
    })
    return () => observer.disconnect()
  }, [ref, name])
  return value
}

export interface OrbProps {
  /** Our vocabulary for what is going on. */
  state?: OrbKind
  /** 20 inline with text (default), 32, or 64 at chat-avatar scale. */
  size?: OrbSize
  /**
   * A CSS custom property to paint with — `--accent-walkthrough` for the walkthrough band,
   * whose live cue owns a colour of its own. Omitted, the orb wears the house ink: foreground
   * at 80%.
   */
  token?: string
  className?: string
  'aria-label'?: string
  'aria-hidden'?: boolean
}

/**
 * Orb — the app's thinking indicator: a dotted sphere from `thinking-orbs`, painted on a plain
 * 2D canvas, in whichever of the library's states says what the app is doing.
 *
 * The library reads a plain colour string, not a token, so the wrapper resolves the token to
 * one and hands it over; the wrapper is also where the token is read from, since it sits in
 * the token scope.
 */
export function Orb({ state = 'thinking', size = 20, token, className, ...rest }: OrbProps) {
  const ref = useRef<HTMLSpanElement | null>(null)
  const ink = useTokenColor(ref, token ?? '--foreground')
  // The weaving orb reads fainter than the rest at the house ink, so the code-generation state
  // alone runs at full strength and one density step up.
  const bright = state === 'generating'

  // The token is read in a LAYOUT effect, so the colour lands before the first paint: the
  // library itself paints only on the client, after its theme has resolved.
  return (
    <span ref={ref} className={cn('inline-flex shrink-0 items-center justify-center', className)}>
      <ThinkingOrb
        state={ORB_STATES[state]}
        size={size}
        theme="auto"
        {...(bright ? { dots: 1.25 } : {})}
        {...(ink ? { color: ink } : {})}
        className={cn('shrink-0', !token && !bright && 'opacity-80')}
        {...rest}
      />
    </span>
  )
}
